import React, { useContext, useState, useEffect } from 'react';
import PageTitle from '../../../Components/PageTitle.js';
import { useNavigate } from 'react-router-dom';
import { useQuery, useLazyQuery } from '@apollo/client';
import {
  subscriptionQueries,
  userQueries
} from '../../../graphql/queries';
import { makeStyles } from '@mui/styles';
import StoreSectionContainer from '../../../Components/Store/StoreSectionContainer.js';
import LoggedOut from './LoggedOut.js';
import GridViewSkeleton from '../../../Components/Skeletons/GridViewSkeleton.js';
import Empty from './Empty.js';
import StoreItems from '../../../Components/Store/StoreItems.js';
import { useTextTranslation } from '../../../Hooks';
import { UserContext, DrawerContext } from '../../../Context';
import {
  formatPurchasesAndSubscriptionsForLibrary,
  getProductListFromPurchases
} from '../../../helpers/purchase.js';

const useStyles = makeStyles((theme) => ({
  items: {
    [theme.breakpoints.down('sm')]: {
      marginBottom: '-8rem'
    }
  },
  avatar: {
    marginRight: '1rem'
  }
}));

export default function Library () {
  const [userPurchases, setUserPurchases] = useState(null);

  const { user, setUser } = useContext(UserContext);
  const { openDrawer } = useContext(DrawerContext);

  const navigate = useNavigate();
  const classes = useStyles();

  const { data: subscriptionData } = useQuery(subscriptionQueries.Subscriptions);
  const [getUserPurchases] = useLazyQuery(userQueries.UserPurchases, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (data.userPurchases) {
        const updatedUser = { ...user };
        // so the 'owned' text shows we need to update the user's products
        updatedUser.products = getProductListFromPurchases(data.userPurchases.purchases);
        // set the userPurchases for use in the grid
        setUserPurchases(data.userPurchases);
        // we also need to update the user in context so the rest of the site knows whats up
        setUser({ ...updatedUser, activeSubscriptions: data.userPurchases.activeSubscriptions });
      }
    },
    onError: (err) => {
      console.log('getUserPurchases err', err);
    }
  });

  // userPurchases is self/admin-only behind the auth gate; a logged-out /library renders
  // <LoggedOut/> and needs no purchase data, so don't fire it anonymously (it self-nulls
  // today and would 401 once the gate enforces — userPurchases must stay locked, not public).
  useEffect(() => {
    if (user) {
      getUserPurchases();
    }
  }, [user, getUserPurchases]);

  const translatedTitle = useTextTranslation('my-library');

  const handleLoginClick = () => {
    openDrawer('login');
  };

  const handleRegisterClick = () => {
    navigate('/register');
  };

  const handleStoreClick = () => {
    navigate('/products');
  };

  if (!user) {
    return (
      <LoggedOut
        onLoginClick={handleLoginClick}
        onRegisterClick={handleRegisterClick}
      />
    );
  }

  if (!subscriptionData || !userPurchases) {
    return <GridViewSkeleton />;
  } else {
    // combine subscriptions and products to form the librarys grid items
    const productsAndSubscriptions = formatPurchasesAndSubscriptionsForLibrary(
      userPurchases,
      subscriptionData.subscriptions
    );

    return (
      <>
        <PageTitle title={translatedTitle} />
        <StoreSectionContainer
          heading={translatedTitle}
          headingTestId='library-heading'
        >
          {
            productsAndSubscriptions && productsAndSubscriptions.length > 0
              ? (
                <div className={classes.items}>
                  <StoreItems
                    products={productsAndSubscriptions}
                    hidePrice
                    itemTestId='library-item'
                  />
                </div>
                )
              : <Empty onStoreClick={handleStoreClick} />
          }
        </StoreSectionContainer>
      </>
    );
  }
}
